import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import apiClient from '../config/api';

interface AgreementDocument {
  document_id: number;
  agreement_id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string;
}

export default function AgreementDocumentsPage() {
  const { agreementId } = useParams<{ agreementId: string }>();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<AgreementDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDocuments() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/api/agreements/${agreementId}/documents`);
        setDocuments(response.data.documents || []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load documents';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }

    if (agreementId) {
      fetchDocuments();
    }
  }, [agreementId]);

  const handleDownload = async (documentId: number, fileName: string) => {
    try {
      const response = await apiClient.get(
        `/api/agreements/${agreementId}/documents/${documentId}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to download document';
      setError(message);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="agreement-documents-page">
      <div className="page-header">
        <h2>Agreement Documents</h2>
        <Button variant="ghost" onClick={() => navigate('/agreements')}>
          Back to Agreements
        </Button>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading documents..." />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Uploaded At</th>
              <th>Uploaded By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-row">
                  No documents found for this agreement.
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr key={doc.document_id}>
                  <td>{doc.file_name}</td>
                  <td>{formatDate(doc.uploaded_at)}</td>
                  <td>{doc.uploaded_by}</td>
                  <td className="actions-cell">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(doc.document_id, doc.file_name)}
                    >
                      Download
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
